<template>
    <div>
        <b-modal
            :visible="visible"
            :id="`article-changelog-modal-${articleId}`"
            size="xl"
            @hidden="hidden"
        >
            <template #modal-title>
                Article Content Changelog - ID: {{ articleId }}, {{ title }}
            </template>

            <div class="mb-2">
                <div v-if="loading" class="mb-3 text-center">
                    <b-spinner label="Spinning"></b-spinner>
                </div>
                <template v-else>
                    <b-alert v-if="errorMessage" show variant="danger">
                        There was a problem loading the changelog for this article.
                    </b-alert>
                    <div
                        v-else-if="logs.length === 0"
                        class="text-center"
                    >
                        No changes logged for this article.
                    </div>
                    <div v-else>
                        <div class="row">
                            <div class="col-md-4">
                                <b-card no-body>
                                    <template #header>
                                        Log Entries
                                    </template>

                                    <b-card-body class="p-1 log-list">
                                        <b-list-group>
                                            <b-list-group-item
                                                v-for="log in logs"
                                                button
                                                :key="log.id"
                                                :active="log.id === selectedLogId"
                                                @click="selectedLogId = log.id"
                                            >
                                                <div class="row">
                                                    <div class="col-6"><strong>{{ log.created_at }}</strong></div>
                                                    <div class="col-6">
                                                        <b-badge
                                                            variant="warning"
                                                            class="m-1 log-badge ml-auto"
                                                            v-if="
                                                                log.metadata &&
                                                                (log.metadata.body_text_changed || log.metadata.question_text_changed)
                                                            "
                                                        >
                                                            Text Changed
                                                        </b-badge>
                                                        <b-badge
                                                            variant="info"
                                                            class="m-1 log-badge ml-auto"
                                                            v-if="log.metadata && log.metadata.question_moved"
                                                        >
                                                            Question Moved
                                                        </b-badge>
                                                        <b-badge
                                                            variant="info"
                                                            class="m-1 log-badge ml-auto"
                                                            v-if="log.metadata && log.metadata.question_added"
                                                        >
                                                            Question Added
                                                        </b-badge>
                                                        <b-badge
                                                            variant="danger"
                                                            class="m-1 log-badge ml-auto"
                                                            v-if="log.metadata && log.metadata.question_removed"
                                                        >
                                                            Question Deleted
                                                        </b-badge>
                                                    </div>
                                                </div>
                                                <div class="row">
                                                    <div class="col-6 p-0">
                                                        <b-badge
                                                            class="m-1 log-badge"
                                                            content="User"
                                                            v-tippy
                                                        >
                                                            <font-awesome-icon
                                                                :icon="['fad', 'user-edit']"
                                                                fixed-width
                                                            />
                                                            {{ log.full_name }}
                                                        </b-badge>
                                                    </div>
                                                    <div class="col-6 p-0">
                                                        <b-badge
                                                            variant="dark"
                                                            class="m-1 log-badge"
                                                            content="Log Type"
                                                            v-tippy
                                                        >
                                                            <font-awesome-icon
                                                                :icon="['fad', 'info-circle']"
                                                                fixed-width
                                                            />
                                                            {{ log.update_type }}
                                                        </b-badge>
                                                    </div>
                                                </div>
                                            </b-list-group-item>
                                        </b-list-group>
                                    </b-card-body>
                                </b-card>

                            </div>
                            <div class="col-md-8">
                                <template v-if="selectedLog">
                                    <b-card no-body>
                                        <template #header>
                                            Selected Log Entry
                                        </template>

                                        <b-card-body class="p-0 log-list">
                                            <b-tabs
                                                card
                                                class="p-0"
                                                content-class="m-0 p-0"
                                            >
                                                <b-tab title="Title">
                                                    <b-input :value="selectedLog.title" readonly />
                                                </b-tab>
                                                <b-tab title="Body">
                                                    <TipTap
                                                        v-model="selectedLog.body"
                                                        readonly
                                                        :key="`${selectedLog.id}-body`"
                                                    />
                                                </b-tab>
                                                <b-tab title="Designer Notes">
                                                    <TipTap
                                                        v-model="selectedLog.designer_notes"
                                                        readonly
                                                        :key="`${selectedLog.id}-notes`"
                                                    />
                                                </b-tab>
                                                <b-tab title="Questions">
                                                    <Question
                                                        class="mb-2"
                                                        :class="{
                                                            'affected-question': (
                                                                selectedLog.metadata
                                                                && selectedLog.metadata.question_id === question.id
                                                            )
                                                        }"
                                                        v-for="question in selectedLog.questions"
                                                        :question="question"
                                                        :publication="{}"
                                                        :unit="{}"
                                                        :disabled="true"
                                                        :hide_standards="true"
                                                    />
                                                </b-tab>
                                            </b-tabs>
                                        </b-card-body>
                                    </b-card>
                                </template>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <template #modal-footer>
                <b-btn @click="hidden()">
                    Close
                </b-btn>
            </template>
        </b-modal>
    </div>
</template>

<style scoped>
    .log-list {
        min-height: 100px;
        max-height: 50vh;
        overflow-y: auto;
    }
    .log-badge {
        font-size: 0.8rem;
    }
    .affected-question {
        border: 2px solid #f00;
    }
</style>

<script>
import TipTap from '@/components/tiptap/TipTap'
import Title from '@/components/Title'
import moment from 'moment'
import Question from "@/components/article/Question.vue";

export default {
    components: {
        Question,
        TipTap,
        Title,
    },
    data: () => ({
        loading: false,
        logs: [],
        selectedLogId: null,
        errorMessage: null,
    }),
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        currentArticle: {
            type: Object,
            default: null,
        },
        title: {
            type: String,
            default: '',
        },
        articleId: {
            type: Number,
            default: null,
        },
        unitId: {
            type: Number,
            default: null,
        },
        publicationId: {
            type: Number,
            default: null,
        },
    },
    computed: {
        selectedLog() {
            return this.logs.find((log) => log.id === this.selectedLogId)
        },
    },
    methods: {
        hidden() {
            this.$emit('update:visible', false)
            this.reset()
        },
        reset() {
            this.errorMessage = null
            this.logs = []
            this.selectedLogId = null
        },
        mapLogType(updateType) {
            switch (updateType) {
                case 'body_changed': return 'Body Updated'
                case 'notes_changed': return 'Notes Updated'
                case 'title_changed': return 'Title Updated'
                case 'questions_changed': return 'Questions Updated'
                case 'body_copied': return 'Copied'
                default: return 'Unknown Update'
            }
        },
        async loadChangeLog() {
            try {
                this.loading = true
                const response = await this.axios.get(
                    '/api/articles/log',
                    {
                        params: {
                            'article_id': this.articleId,
                            'unit_id': this.unitId,
                            'publication_id': this.publicationId,
                        }
                    }
                )
                if (response.data) {
                    this.logs = response.data.map(
                        log => ({
                            ...log,
                            update_type: this.mapLogType(log.update_type),
                            created_at: moment(log.created_at).format('D-M-YY H:m'),
                        })
                    )
                }

                if (this.logs.length > 0) {
                    this.selectedLogId = this.logs[0].id
                }
            } catch (e) {
                this.errorMessage = e.message
            } finally {
                this.loading = false
            }
        },
    },
    mounted() {
        this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
            if (modalId !== `article-changelog-modal-${this.articleId}`) {
                return
            }

            this.reset()
            this.loadChangeLog()
        })
    }
}
</script>
